'use client';

// Settings → Release switches (admin): who may use each capability that needs its own release
// decision. The server enforces them on the API and in the desk; every change is recorded with who.
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

type Level = 'off' | 'admin' | 'members' | 'everyone';
interface Switch { name: string; label: string; what: string; default: Level; level: Level; updatedBy?: string; updatedAt?: string }

const LEVELS: { id: Level; label: string }[] = [
  { id: 'off', label: 'Off' },
  { id: 'admin', label: 'Admin only' },
  { id: 'members', label: 'Approved members' },
  { id: 'everyone', label: 'Everyone signed in' },
];
const labelOf = (l: Level) => LEVELS.find((x) => x.id === l)?.label ?? l;

export function ReleaseSwitches() {
  const [rows, setRows] = useState<Switch[] | null>(null);
  useEffect(() => {
    void api('/v1/admin/features').then((r) => (r.ok ? r.json() : null)).then((j) => setRows(j?.features ?? [])).catch(() => setRows([]));
  }, []);
  const set = async (name: string, level: Level) => {
    const r = await api(`/v1/admin/features/${name}`, { method: 'PUT', body: JSON.stringify({ level }) });
    const j = await r.json().catch(() => null);
    if (!r.ok) { toast.error(j?.error ?? 'Could not change it'); return; }
    setRows(j.features);
    toast.success(`${j.features.find((f: Switch) => f.name === name)?.label ?? name}: ${labelOf(level)}`);
  };
  if (!rows || rows.length === 0) return null;
  return (
    <Card className="mt-6 gap-0 px-6 py-0 md:px-8">
      <div className="border-b py-5">
        <h2 className="text-[15px] font-semibold">Release switches</h2>
        <p className="mt-0.5 text-[13px] text-muted-foreground">Who may use each recommendation-like capability. Enforced by the server (the API and the desk); a change applies within a minute. &ldquo;Everyone signed in&rdquo; includes accounts awaiting approval. Switch a capability to everyone only once its legal review allows it (docs/legal/legal-review-brief.md).</p>
      </div>
      {rows.map((f) => (
        <div key={f.name} className="flex flex-wrap items-start justify-between gap-3 border-b py-4 last:border-b-0">
          <div className="min-w-0 max-w-[560px]">
            <div className="font-semibold text-ink">{f.label}</div>
            <p className="text-[13px] text-muted-foreground">{f.what}</p>
            <p className="mt-0.5 text-[11.5px] text-muted-foreground">
              Default: {labelOf(f.default)}{f.updatedBy && f.updatedAt ? ` · set by ${f.updatedBy} on ${new Date(f.updatedAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}` : ''}
            </p>
          </div>
          <Select value={f.level} onValueChange={(v) => void set(f.name, v as Level)}>
            <SelectTrigger aria-label={`${f.label}: who may use it`} className="h-9 w-48 rounded-full bg-card font-medium"><SelectValue /></SelectTrigger>
            <SelectContent>{LEVELS.map((l) => <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      ))}
    </Card>
  );
}
